import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { NODE_CATALOG } from '@loggy/shared';
import {
  Alert,
  Button,
  Card,
  Field,
  Heading,
  Inline,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
} from '../design/index.js';
import { createNode, deleteNode, updateNode } from '../api.js';
import type { NodeSummary } from '../api.js';
import styles from './NodeForm.module.css';

export interface NodeFormProps {
  /** Omit to add a new node. Pass one to rename it and offer deleting it. */
  node?: NodeSummary | null;
  onSaved: (node: NodeSummary) => void;
  onDeleted?: (nodeId: string) => void;
  onCancel?: () => void;
  className?: string;
}

type Phase =
  | { kind: 'editing' }
  | { kind: 'submitting' }
  | { kind: 'delete-confirm' }
  | { kind: 'deleting' };

/** Title-cases a catalog `category` for display — the catalog itself has no
 *  human label for these, only the machine-friendly key. */
function categoryLabel(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1);
}

/** Groups the catalog by whichever `category` values are actually present,
 *  derived at render time. Never a hardcoded category list: adding a node
 *  type is meant to stay a one-line catalog edit (see CLAUDE.md, "Adding
 *  something new to track"), and a hardcoded list here would quietly turn
 *  that into a two-file edit. */
function groupByCategory() {
  const groups = new Map<string, typeof NODE_CATALOG[number][]>();
  for (const definition of NODE_CATALOG) {
    const existing = groups.get(definition.category);
    if (existing) existing.push(definition);
    else groups.set(definition.category, [definition]);
  }
  return [...groups.entries()];
}

/** Names what goes with a delete, so the confirmation is never a bare
 *  "are you sure" — the reader sees exactly what history is at stake. */
function deletePrompt(title: string, entryCount: number): string {
  if (entryCount === 0) return `Delete ${title}? It has no readings yet.`;
  const noun = entryCount === 1 ? 'reading' : 'readings';
  return `Delete ${title} and its ${entryCount} ${noun}? This cannot be undone.`;
}

/**
 * Add-a-node / edit-a-node, in one form.
 *
 * Add mode picks a catalog entry (grouped by category) and an optional
 * title; edit mode only ever renames, because a node's type is fixed at
 * creation — the catalog entry decides its kind, unit and Withings mapping,
 * none of which could survive being swapped later without orphaning history.
 */
export function NodeForm({ node, onSaved, onDeleted, onCancel, className }: NodeFormProps) {
  const isEditing = node != null;
  const grouped = useMemo(groupByCategory, []);

  const [typeKey, setTypeKey] = useState(node?.typeKey ?? NODE_CATALOG[0]?.key ?? '');
  const [title, setTitle] = useState(node?.title ?? '');

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const selectedDefinition = NODE_CATALOG.find((definition) => definition.key === typeKey);
  const busy = phase.kind === 'submitting' || phase.kind === 'deleting';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    const trimmedTitle = title.trim();

    if (isEditing) {
      if (!trimmedTitle) {
        setSubmitError('Give this node a title.');
        return;
      }
      setPhase({ kind: 'submitting' });
      setSubmitError(null);
      try {
        const saved = await updateNode(node.id, { title: trimmedTitle });
        setPhase({ kind: 'editing' });
        onSaved(saved);
      } catch (err) {
        setPhase({ kind: 'editing' });
        setSubmitError(err instanceof Error ? err.message : 'Could not rename this node.');
      }
      return;
    }

    if (!typeKey) {
      setSubmitError('Pick what to track.');
      return;
    }
    setPhase({ kind: 'submitting' });
    setSubmitError(null);
    try {
      const saved = await createNode({ typeKey, title: trimmedTitle || undefined });
      setPhase({ kind: 'editing' });
      onSaved(saved);
    } catch (err) {
      setPhase({ kind: 'editing' });
      setSubmitError(err instanceof Error ? err.message : 'Could not add this node.');
    }
  }

  async function handleDelete() {
    if (!node) return;
    setPhase({ kind: 'deleting' });
    setDeleteError(null);
    try {
      await deleteNode(node.id);
      onDeleted?.(node.id);
    } catch (err) {
      setPhase({ kind: 'delete-confirm' });
      setDeleteError(err instanceof Error ? err.message : 'Could not delete this node.');
    }
  }

  return (
    <Card className={className} data-testid={isEditing ? 'node-form-edit' : 'node-form-create'}>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap={5}>
          <Heading level={3} size="lg">{isEditing ? 'Edit node' : 'Add something to track'}</Heading>

          {!isEditing && (
            <Field label="What to track" required hint={selectedDefinition?.description}>
              <Select
                data-testid="node-form-type"
                value={typeKey}
                onChange={(event) => {
                  setTypeKey(event.target.value);
                }}
                disabled={busy}
                required
              >
                {grouped.map(([category, definitions]) => (
                  <optgroup key={category} label={categoryLabel(category)}>
                    {definitions.map((definition) => (
                      <option key={definition.key} value={definition.key}>
                        {definition.title}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </Field>
          )}

          <Field
            label="Title"
            hint={!isEditing ? 'Leave blank to use the default name shown above.' : undefined}
          >
            <Input
              data-testid="node-form-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={selectedDefinition?.title}
              disabled={busy}
              required={isEditing}
            />
          </Field>

          {submitError && (
            <Alert
              variant="error"
              title={isEditing ? "Couldn't rename this node" : "Couldn't add this node"}
              data-testid="node-form-error"
            >
              {submitError}
            </Alert>
          )}

          <Inline gap={3} align="center" wrap>
            <Button type="submit" data-testid="node-form-submit" disabled={busy}>
              {phase.kind === 'submitting' ? 'Saving…' : isEditing ? 'Save changes' : 'Add node'}
            </Button>
            {phase.kind === 'submitting' && (
              <Spinner size="sm" aria-label="Saving this node" data-testid="node-form-saving" />
            )}
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                data-testid="node-form-cancel"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
          </Inline>

          {isEditing && node && (
            <div className={styles.deleteZone}>
              {phase.kind !== 'delete-confirm' && phase.kind !== 'deleting' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="node-form-delete-button"
                  onClick={() => setPhase({ kind: 'delete-confirm' })}
                >
                  Delete node
                </Button>
              ) : (
                <Stack gap={3} data-testid="node-form-delete-confirm">
                  <Text size="sm" color="secondary">
                    {deletePrompt(node.title, node.entryCount)}
                  </Text>
                  {node.source === 'withings' && (
                    <Text size="sm" color="secondary">
                      Withings still holds this history — re-adding this node pulls it back.
                    </Text>
                  )}
                  <Inline gap={3} align="center" wrap>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      data-testid="node-form-delete-confirm-button"
                      onClick={handleDelete}
                      disabled={phase.kind === 'deleting'}
                    >
                      {phase.kind === 'deleting' ? 'Deleting…' : 'Yes, delete'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={phase.kind === 'deleting'}
                      onClick={() => setPhase({ kind: 'editing' })}
                    >
                      Cancel
                    </Button>
                  </Inline>
                </Stack>
              )}
              {deleteError && (
                <Alert variant="error" title="Couldn't delete this node" data-testid="node-form-delete-error">
                  {deleteError}
                </Alert>
              )}
            </div>
          )}
        </Stack>
      </form>
    </Card>
  );
}
